/**
 * Shared date formatting.
 *
 * Dates were rendered eight different ways across the app — en-GB, en-US, locale-less, and two table columns that printed the raw ISO string
 * ("2026-07-01"). Day-first with a short month is used here because it's
 * already the dominant idiom in the codebase, it's unambiguous (unlike
 * numeric dd/mm vs mm/dd). The locale comes from the user's settings.
 *
 * Note: input[type="date"] values must stay ISO — use the raw value there,
 * not these helpers.
 */

import { getAppLocale } from './locale';

const DATE_OPTS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

const DATE_TIME_OPTS: Intl.DateTimeFormatOptions = {
  ...DATE_OPTS,
  hour: '2-digit',
  minute: '2-digit',
};

/** A date value as a local Date (a bare YYYY-MM-DD is that day here, not UTC midnight), or null. */
export function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  // A bare 'YYYY-MM-DD' is parsed as UTC midnight, which renders as the
  // previous day for anyone behind UTC — pin it to local time instead.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/** "1 Jul 2026", or `fallback` when the value is missing/unparseable. */
export function formatDate(
  value: string | number | Date | null | undefined,
  fallback = '—'
): string {
  const d = toDate(value);
  return d ? d.toLocaleDateString(getAppLocale(), DATE_OPTS) : fallback;
}

/** "1 Jul 2026, 14:30" — for audit trails and timestamps. */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  fallback = '—'
): string {
  const d = toDate(value);
  return d ? d.toLocaleString(getAppLocale(), DATE_TIME_OPTS) : fallback;
}

/** "Wednesday 1 July 2026" — headings and confirmation screens. */
export function formatDateLong(
  value: string | number | Date | null | undefined,
  fallback = '—'
): string {
  const d = toDate(value);
  return d
    ? d.toLocaleDateString(getAppLocale(), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : fallback;
}

/** "1 Jul" — compact, when the year is obvious from context. */
export function formatDayMonth(
  value: string | number | Date | null | undefined,
  fallback = '—'
): string {
  const d = toDate(value);
  return d ? d.toLocaleDateString(getAppLocale(), { day: 'numeric', month: 'short' }) : fallback;
}

/** "Wed, 1 Jul" — a day in a schedule. */
export function formatWeekdayDayMonth(
  value: string | number | Date | null | undefined,
  fallback = '—'
): string {
  const d = toDate(value);
  return d ? d.toLocaleDateString(getAppLocale(), { weekday: 'short', day: 'numeric', month: 'short' }) : fallback;
}

/** "14:30" (or "2:30 pm", per locale) — a time of day. */
export function formatTime(
  value: string | number | Date | null | undefined,
  fallback = '—'
): string {
  const d = toDate(value);
  return d ? d.toLocaleTimeString(getAppLocale(), { hour: '2-digit', minute: '2-digit' }) : fallback;
}

/** "July 2026" — a month heading. */
export function formatMonthYear(
  value: string | number | Date | null | undefined,
  fallback = '—'
): string {
  const d = toDate(value);
  return d ? d.toLocaleDateString(getAppLocale(), { month: 'long', year: 'numeric' }) : fallback;
}
